import { useEffect, useState } from "react";
import { Check, Loader2, Cloud, AlertTriangle } from "lucide-react";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";

export type ActivationStatus = "provisioning" | "ready" | "failed" | "timeout";

/** Presentational stages — the backend reports a binary provisioning→ready, so
 *  these advance on a timer to make the wait feel responsive. */
const STAGES = [
  "Provisioning database",
  "Configuring authentication",
  "Starting services",
  "Finalizing setup",
];
const PROVISION_STEP_MS = 1500; // advance promptly through early stages
const COMPLETE_STEP_MS = 350; // tick through remaining stages on completion

/**
 * Inline progress card shown while OptiDev Cloud activates from the agent chat.
 * Animates in, runs a staged checklist + progress bar (neutral, theme-adaptive),
 * and eases to a green "activated" state — completing stages sequentially rather
 * than snapping to done.
 */
export function OptiDevCloudActivating({ status }: { status: ActivationStatus }) {
  // Number of COMPLETED stages (0..STAGES.length).
  const [step, setStep] = useState(0);

  const errored = status === "failed" || status === "timeout";

  useEffect(() => {
    if (status === "provisioning") {
      // Advance steadily, but hold the last stage "in progress" until ready.
      const t = setInterval(
        () => setStep((s) => Math.min(s + 1, STAGES.length - 1)),
        PROVISION_STEP_MS,
      );
      return () => clearInterval(t);
    }
    if (status === "ready") {
      // Complete remaining stages one-by-one so it never snaps to the end.
      const t = setInterval(() => {
        setStep((s) => {
          if (s >= STAGES.length) {
            clearInterval(t);
            return s;
          }
          return s + 1;
        });
      }, COMPLETE_STEP_MS);
      return () => clearInterval(t);
    }
  }, [status]);

  const allDone = status === "ready" && step >= STAGES.length;
  const pct = errored
    ? 100
    : Math.min(100, Math.round((step / STAGES.length) * 100));

  return (
    <Card
      className={cn(
        "w-full max-w-md p-3.5 transition-colors duration-500",
        "animate-in fade-in slide-in-from-bottom-1",
        allDone
          ? "border-emerald-500/40 bg-emerald-500/5"
          : errored
            ? "border-destructive/40 bg-destructive/5"
            : "border-border bg-muted/20",
      )}
    >
      <div className="mb-2.5 flex items-center gap-2">
        {allDone ? (
          <Check className="h-4 w-4 text-emerald-500" strokeWidth={3} />
        ) : errored ? (
          <AlertTriangle className="h-4 w-4 text-destructive" />
        ) : (
          <Cloud className="h-4 w-4 text-foreground" />
        )}
        <span className="text-sm font-semibold text-foreground">
          {allDone
            ? "OptiDev Cloud activated"
            : errored
              ? "Activation issue"
              : "Activating OptiDev Cloud"}
        </span>
      </div>

      {/* Progress bar (neutral while running, green when done) */}
      <div className="mb-3 h-1.5 w-full overflow-hidden rounded-full bg-border">
        <div
          className={cn(
            "h-full rounded-full transition-all duration-500 ease-out",
            errored ? "bg-destructive" : allDone ? "bg-emerald-500" : "bg-foreground",
          )}
          style={{ width: `${pct}%` }}
        />
      </div>

      {errored ? (
        <div className="text-sm text-muted-foreground">
          {status === "timeout"
            ? "Taking longer than expected — check Project Settings."
            : "Activation failed. Please try again."}
        </div>
      ) : (
        <ul className="space-y-1.5">
          {STAGES.map((label, i) => {
            const isDone = i < step;
            const isCurrent = !allDone && i === step;
            return (
              <li
                key={label}
                className={cn(
                  "flex items-center gap-2 text-sm transition-colors",
                  isDone || isCurrent
                    ? "text-foreground"
                    : "text-muted-foreground/60",
                )}
              >
                <span className="flex h-4 w-4 shrink-0 items-center justify-center">
                  {isDone ? (
                    <Check className="h-3.5 w-3.5 text-emerald-500" strokeWidth={3} />
                  ) : isCurrent ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin text-foreground" />
                  ) : (
                    <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/30" />
                  )}
                </span>
                {label}
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}
